/**
 * Allocator FEFO (First Expired, First Out) interno.
 *
 * Los lotes se consumen en orden:
 *   1. expires_at ASC NULLS LAST  (vence antes → se consume primero; sin vencimiento va al final)
 *   2. received_at ASC             (más antiguo primero, desempate)
 *   3. id ASC                      (desempate final, determinístico)
 *
 * Los lotes "vendibles" son los que tienen remaining_quantity > 0 y
 * (expires_at IS NULL OR expires_at > ahora).
 *
 * "Cantidad vendible" = suma de remaining_quantity de lotes no vencidos.
 * Si no existen lotes para el par (product, branch), devuelve null para
 * que el llamador use el fallback de stock agregado.
 */

import { type SqliteAsyncConnection } from '../../../../shared/localDb';
import { getCatalogRamSnapshotCache } from '../../../../catalog/infra/catalogRamSnapshotCache';
import { allocateLotsByFefoOrder, type FefoLotRow } from '../../domain/fefoAllocationOrder';

export type LotAllocation = {
  lotId: string;
  quantity: number;
  unitCost: number;
  expiresAt: string | null;
};

export type AppliedLotAllocation = {
  allocationId: string;
  lotId: string;
  referenceType: string;
  referenceId: string;
  allocatedQuantity: number;
  createdAt: string;
};

type LotDbRow = FefoLotRow;

export type FefoAllocationMode =
  /** FEFO puro: solo lotes vendibles (ventas, consumos normales). */
  | 'fefo'
  /**
   * Vencidos primero, luego FEFO normal. Para ajustes negativos manuales:
   * si faltan unidades físicas, las vencidas son las que menos probable es
   * que sigan en góndola — y además auto-cura divergencias agregado↔lotes.
   */
  | 'expiredFirst';

/**
 * Calcula las asignaciones FEFO necesarias para consumir `quantityNeeded`
 * unidades de un producto en una sucursal.
 *
 * - 'fefo': solo lotes no vencidos (vendibles). Si la cantidad vendible total
 *   es menor a `quantityNeeded`, las asignaciones cubren solo la parte vendible.
 * - 'expiredFirst': consume primero los lotes vencidos (expires_at más antiguo
 *   primero) y después sigue el orden FEFO sobre los vendibles.
 *
 * Retorna array vacío si no hay lotes, para que el llamador use fallback.
 */
export const planFefoAllocations = async (
  productId: string,
  branchId: string,
  quantityNeeded: number,
  mode: FefoAllocationMode = 'fefo',
  _connection?: SqliteAsyncConnection
): Promise<LotAllocation[]> => {
  // ADR-017 Fase 4: SQLite local eliminado — leer stockLots de catalogRamSnapshotCache.
  const now = new Date().toISOString();
  const includeExpired = mode === 'expiredFirst';
  const allLots = getCatalogRamSnapshotCache()?.stockLots ?? [];
  const lots: LotDbRow[] = allLots
    .filter(
      (l) =>
        l.product_id === productId &&
        l.branch_id === branchId &&
        l.remaining_quantity > 0 &&
        !l.depleted_at &&
        (includeExpired || l.expires_at === null || l.expires_at > now)
    )
    .map((l) => ({
      id: l.id,
      remaining_quantity: l.remaining_quantity,
      unit_cost: l.unit_cost,
      expires_at: l.expires_at,
    }))
    .sort((a, b) => {
      if (includeExpired) {
        // Vencidos primero (más antiguo primero), después FEFO normal.
        const aExpired = a.expires_at !== null && a.expires_at <= now;
        const bExpired = b.expires_at !== null && b.expires_at <= now;
        if (aExpired !== bExpired) return aExpired ? -1 : 1;
      }
      // expires_at ASC NULLS LAST
      if (a.expires_at === null && b.expires_at !== null) return 1;
      if (a.expires_at !== null && b.expires_at === null) return -1;
      if (a.expires_at && b.expires_at && a.expires_at !== b.expires_at) {
        return a.expires_at < b.expires_at ? -1 : 1;
      }
      // received_at ASC (no está en FefoLotRow, desempate por id)
      return a.id < b.id ? -1 : 1;
    });

  if (lots.length === 0) return [];

  return allocateLotsByFefoOrder(lots, quantityNeeded);
};

/**
 * Escribe las asignaciones en DB:
 *   - Inserta una fila en stock_lot_allocations por lote consumido.
 *   - Reduce remaining_quantity en cada lote.
 *   - Marca depleted_at si el lote quedó en 0.
 */
export const applyFefoAllocations = async (
  allocations: LotAllocation[],
  referenceType: string,
  referenceId: string,
  createdAt: string,
  _connection?: SqliteAsyncConnection
): Promise<AppliedLotAllocation[]> => {
  // ADR-017 Fase 4: SQLite local eliminado — las asignaciones se persisten via sync command al servidor.
  const applied: AppliedLotAllocation[] = [];
  for (const alloc of allocations) {
    const allocationId = crypto.randomUUID();
    applied.push({
      allocationId,
      lotId: alloc.lotId,
      referenceType,
      referenceId,
      allocatedQuantity: alloc.quantity,
      createdAt,
    });
  }
  return applied;
};

export type StockLotExpiryRow = {
  lotId: string;
  expiresAt: string | null;
  remainingQuantity: number;
  expired: boolean;
};

export type StockLotExpirySplit = {
  /** Unidades vendibles (lotes activos no vencidos). */
  sellableQuantity: number;
  /** Unidades vencidas con remaining > 0 (el agregado las sigue contando). */
  expiredQuantity: number;
  /** Lotes activos en orden FEFO (expires_at ASC NULLS LAST, id desempate). */
  lots: StockLotExpiryRow[];
};

// Una FlatList pide el split por fila; cachear por (product,branch) mientras la
// referencia del array stockLots del snapshot no cambie (WeakMap → GC al refresh).
const lotSplitCache = new WeakMap<object, Map<string, StockLotExpirySplit | null>>();

const computeStockLotExpirySplit = (
  allLots: readonly {
    id: string;
    product_id: string;
    branch_id: string;
    remaining_quantity: number;
    expires_at: string | null;
    depleted_at: string | null;
  }[],
  productId: string,
  branchId: string,
  now: string
): StockLotExpirySplit | null => {
  const matching = allLots.filter(
    (l) =>
      l.product_id === productId &&
      l.branch_id === branchId &&
      l.remaining_quantity > 0 &&
      !l.depleted_at
  );
  if (matching.length === 0) return null;
  const lots: StockLotExpiryRow[] = matching
    .map((l) => ({
      lotId: l.id,
      expiresAt: l.expires_at ?? null,
      remainingQuantity: l.remaining_quantity,
      expired: l.expires_at != null && l.expires_at <= now,
    }))
    .sort((a, b) => {
      if (a.expiresAt === null && b.expiresAt !== null) return 1;
      if (a.expiresAt !== null && b.expiresAt === null) return -1;
      if (a.expiresAt && b.expiresAt && a.expiresAt !== b.expiresAt) {
        return a.expiresAt < b.expiresAt ? -1 : 1;
      }
      return a.lotId < b.lotId ? -1 : 1;
    });
  let sellableQuantity = 0;
  let expiredQuantity = 0;
  for (const lot of lots) {
    if (lot.expired) expiredQuantity += lot.remainingQuantity;
    else sellableQuantity += lot.remainingQuantity;
  }
  return { sellableQuantity, expiredQuantity, lots };
};

/**
 * Split vendible/vencido de los lotes activos (remaining>0, sin depleted) de
 * un producto en una sucursal. Sincrónico: lee el snapshot RAM.
 *
 * FEFO es a nivel producto (ADR-016/#688): las celdas de variante no tienen
 * lotes propios — el llamador decide si la fila aplica (parent) o no.
 *
 * - Sin lotes → null (fallback a stocks.quantity/expires_at agregado).
 */
export const getStockLotExpirySplit = (
  productId: string,
  branchId: string
): StockLotExpirySplit | null => {
  const allLots = getCatalogRamSnapshotCache()?.stockLots;
  if (!allLots || allLots.length === 0) return null;
  let cache = lotSplitCache.get(allLots);
  if (!cache) {
    cache = new Map();
    lotSplitCache.set(allLots, cache);
  }
  const cacheKey = `${productId}|${branchId}`;
  if (cache.has(cacheKey)) return cache.get(cacheKey) ?? null;
  const split = computeStockLotExpirySplit(
    allLots,
    productId,
    branchId,
    new Date().toISOString()
  );
  cache.set(cacheKey, split);
  return split;
};

/**
 * Devuelve la cantidad vendible (no vencida) de lotes para un producto+sucursal.
 *
 * - Si no existen lotes → null (usar fallback de stocks.quantity).
 * - Si existen lotes → número (puede ser 0 si todos vencidos).
 */
export const getSellableLotQuantity = async (
  productId: string,
  branchId: string,
  _connection?: SqliteAsyncConnection
): Promise<number | null> => {
  // ADR-017 Fase 4: SQLite local eliminado — leer stockLots de catalogRamSnapshotCache.
  return getStockLotExpirySplit(productId, branchId)?.sellableQuantity ?? null;
};

/**
 * Devuelve el próximo vencimiento (expires_at) entre los lotes activos de un producto+sucursal.
 * Devuelve null si no hay lotes o ninguno tiene expires_at.
 */
export const getNextExpiryFromLots = async (
  productId: string,
  branchId: string,
  _connection?: SqliteAsyncConnection
): Promise<string | null> => {
  // ADR-017 Fase 4: SQLite local eliminado — leer de catalogRamSnapshotCache.
  const allLots = getCatalogRamSnapshotCache()?.stockLots ?? [];
  const matching = allLots
    .filter((l) => l.product_id === productId && l.branch_id === branchId && l.remaining_quantity > 0 && l.expires_at)
    .map((l) => l.expires_at)
    .filter((v): v is string => v !== null);
  if (matching.length === 0) return null;
  return matching.sort()[0];
};

/**
 * Inserta un lote nuevo en stock_lots y encola su sincronización.
 * Retorna el id del lote creado.
 */
export const insertStockLot = async (params: {
  productId: string;
  branchId: string;
  receivedAt: string;
  expiresAt: string | null;
  unitCost: number;
  quantity: number;
  sourceType: string;
  sourceRefId: string | null;
  lotNumber?: string | null;
}, _connection?: SqliteAsyncConnection): Promise<string> => {
  // ADR-017 Fase 4: SQLite local eliminado — el lote se persiste via sync command al servidor.
  return crypto.randomUUID();
};
